import { defaultAxiosInstance } from '../config/axiosConfig';

// Build the shared pagination/search query params (omitting empties).
const listParams = ({ search, limit, offset } = {}) => {
  const params = {};
  if (search) params.search = search;
  if (limit != null) params.limit = limit;
  if (offset != null) params.offset = offset;
  return params;
};

const hasValue = (v) => v !== null && v !== undefined && v !== '';

/**
 * Partner admin area service (Partner tier guide §5).
 *
 * Auth: the partner staff member's own x-api-key. Every endpoint needs its
 * partner_* key AND membership of the partner's home company, and is scoped to
 * the caller's partner by the backend: another partner's company or user
 * returns 404 — treat 404 as "no longer yours" and refresh.
 *
 * All methods return `response.data`; callers normalize shape.
 */
class PartnerAdminService {
  // ── Overview (§5.1, any partner key) ─────────────────────────────────────
  /** → { partner: {...counts}, company_permission_keys, partner_permission_keys } */
  async getMe() {
    const res = await defaultAxiosInstance.get('/partner-admin/me');
    return res.data;
  }

  // ── Companies (§5.2, partner_companies) ──────────────────────────────────
  /** Client companies only — the home company never appears. → { companies, total, ... } */
  async listCompanies({ activeOnly = false, ...opts } = {}) {
    const res = await defaultAxiosInstance.get('/partner-admin/companies', {
      params: { active_only: activeOnly, ...listParams(opts) },
    });
    return res.data;
  }

  /** Body: { name, is_active? }. Names are unique platform-wide. */
  async createCompany(payload) {
    const res = await defaultAxiosInstance.post('/partner-admin/companies', payload);
    return res.data;
  }

  async getCompany(companyId) {
    const res = await defaultAxiosInstance.get(`/partner-admin/companies/${companyId}`);
    return res.data;
  }

  /** Body: any subset of { name, is_active }. */
  async updateCompany(companyId, payload) {
    const res = await defaultAxiosInstance.patch(`/partner-admin/companies/${companyId}`, payload);
    return res.data;
  }

  // ── Feature limits (§5.3, partner_entitlements) ──────────────────────────
  /** What the partner may offer. → { company_id (home — ignore), permission_keys } */
  async getPlan() {
    const res = await defaultAxiosInstance.get('/partner-admin/entitlements');
    return res.data;
  }

  /** → { company_id, permission_keys } */
  async getCompanyEntitlements(companyId) {
    const res = await defaultAxiosInstance.get(`/partner-admin/companies/${companyId}/entitlements`);
    return res.data;
  }

  /** Declarative; must be a subset of the plan minus usage_run_content. */
  async setCompanyEntitlements(companyId, permissionKeys) {
    const res = await defaultAxiosInstance.put(`/partner-admin/companies/${companyId}/entitlements`, {
      permission_keys: permissionKeys,
    });
    return res.data;
  }

  // ── Users (§5.4, partner_users) ──────────────────────────────────────────
  /** Without companyId: all companies incl. direct users. → { users, total, ... } */
  async listUsers({ companyId = null, ...opts } = {}) {
    const params = listParams(opts);
    if (hasValue(companyId)) params.company_id = companyId;
    const res = await defaultAxiosInstance.get('/partner-admin/users', { params });
    return res.data;
  }

  /**
   * Body: { email, first_name, last_name, platform?, company_id?, role_ids }.
   * Omitting company_id creates a direct user. → { user, api_key (SHOWN ONCE), roles }
   */
  async createUser(payload) {
    const res = await defaultAxiosInstance.post('/partner-admin/users', payload);
    return res.data;
  }

  /** Body: any subset of { is_active, company_id }. Moving drops company_admin + custom roles. */
  async updateUser(userId, payload) {
    const res = await defaultAxiosInstance.patch(`/partner-admin/users/${userId}`, payload);
    return res.data;
  }

  async assignUserRole(userId, roleId) {
    const res = await defaultAxiosInstance.post(`/partner-admin/users/${userId}/roles`, {
      role_id: roleId,
    });
    return res.data;
  }

  async removeUserRole(userId, roleId) {
    const res = await defaultAxiosInstance.delete(`/partner-admin/users/${userId}/roles/${roleId}`);
    return res.data;
  }

  /** → { user_id, api_key (SHOWN ONCE) } */
  async resetUserApiKey(userId) {
    const res = await defaultAxiosInstance.post(`/partner-admin/users/${userId}/reset-api-key`);
    return res.data;
  }

  /** Roles assignable in a company; omit companyId for direct users. → Role[] or { roles } */
  async listRoles(companyId = null) {
    const params = {};
    if (hasValue(companyId)) params.company_id = companyId;
    const res = await defaultAxiosInstance.get('/partner-admin/roles', { params });
    return res.data;
  }

  // ── LLM keys (§5.5, partner_llm_keys) ────────────────────────────────────
  /** The partner's own keys. → { credentials, total } */
  async getOwnKeys() {
    const res = await defaultAxiosInstance.get('/partner-admin/llm-keys');
    return res.data;
  }

  /** Body: { api_key, base_url? } only — anything else is 422; blank key not allowed. */
  async setOwnKey(providerId, payload) {
    const res = await defaultAxiosInstance.put(`/partner-admin/llm-keys/${providerId}`, payload);
    return res.data;
  }

  async clearOwnKey(providerId) {
    const res = await defaultAxiosInstance.delete(`/partner-admin/llm-keys/${providerId}`);
    return res.data;
  }

  /** A client company's rows. No row for a provider = inherits the partner's key. */
  async getCompanyKeys(companyId) {
    const res = await defaultAxiosInstance.get(`/partner-admin/companies/${companyId}/llm-keys`);
    return res.data;
  }

  /** Body: at least one of { api_key ('' clears), base_url, inherit_from_partner }. */
  async setCompanyKey(companyId, providerId, payload) {
    const res = await defaultAxiosInstance.put(
      `/partner-admin/companies/${companyId}/llm-keys/${providerId}`,
      payload
    );
    return res.data;
  }

  /** Clears the key but keeps the inheritance choice. */
  async clearCompanyKey(companyId, providerId) {
    const res = await defaultAxiosInstance.delete(
      `/partner-admin/companies/${companyId}/llm-keys/${providerId}`
    );
    return res.data;
  }

  /** Every provider (key matrices render one row per provider). */
  async listProviders() {
    const res = await defaultAxiosInstance.get('/llm-providers/', {
      params: { active_only: true, limit: 200 },
    });
    return res.data?.providers || res.data || [];
  }

  // ── Sharing (§5.6, partner_sharing) ──────────────────────────────────────
  /**
   * What partner staff can share, across all the partner's companies.
   * → { items: [{ resource_type, resource_id, resource, owner, share_count,
   *               partner_share_count, owner_company_id }], total }
   */
  async listResources({ resourceType, companyId = null, userId = null, ...opts } = {}) {
    const params = { resource_type: resourceType, ...listParams(opts) };
    if (hasValue(companyId)) params.company_id = companyId;
    if (hasValue(userId)) params.user_id = userId;
    const res = await defaultAxiosInstance.get('/partner-admin/resources', { params });
    return res.data;
  }

  /** The partner's cross-company shares. → { shares, total } */
  async listShares({ resourceType = null, ...opts } = {}) {
    const params = listParams(opts);
    if (hasValue(resourceType)) params.resource_type = resourceType;
    const res = await defaultAxiosInstance.get('/partner-admin/shares', { params });
    return res.data;
  }

  // ── Audit (§5.8, partner_audit) ──────────────────────────────────────────
  /** Everything done to the partner or any of its companies, by anyone. → { logs, total } */
  async listAuditLogs({ action = null, search = null, limit = 50, offset = 0 } = {}) {
    const params = { limit, offset };
    if (action) params.action = action;
    if (search) params.search = search;
    const res = await defaultAxiosInstance.get('/partner-admin/audit-logs', { params });
    return res.data;
  }
}

const partnerAdminService = new PartnerAdminService();
export default partnerAdminService;
